import { EdgeConditionDTO, SoilConditionDTO, MaterialConditionDTO } from '../../api/generated';
import { BillableMaterial, ConditionSet, MaterialPrediction, PredictionData } from './types';

export function resolveBillableMaterials(
  configuredBMs: BillableMaterial[],
  prediction: PredictionData
): BillableMaterial[] {
  if (configuredBMs.length === 0 || !prediction) return []; // no billable materials configured

  return configuredBMs
    .map((billableMaterial) => {
      const matchingConditionSet = billableMaterial.conditionSets.filter((cs) => evaluateConditionSet(cs, prediction));

      if (matchingConditionSet.length === 0) return null;

      return {
        ...billableMaterial,
        conditionSets: matchingConditionSet,
      };
    })
    .filter((bm): bm is BillableMaterial => bm !== null);
}

function evaluateConditionSet(cs: ConditionSet, prediction: PredictionData) {
  if (cs.conditions.length === 0) return false;

  return cs.conditions.every((condition) => {
    switch (condition.type) {
      case 'material':
        return evaluateMaterialCondition(condition, prediction.material);
      case 'soil':
        return evaluateSoilCondition(condition, prediction.soil);
      case 'edge':
        return evaluateEdgeCondition(condition, prediction.edges);
      default:
        return false;
    }
  });
}

function evaluateMaterialCondition(condition: MaterialConditionDTO, prediction?: MaterialPrediction) {
  return (condition.className === prediction?.className && condition.probability <= prediction?.probability) ?? false;
}

export function evaluateSoilCondition(
  condition: SoilConditionDTO,
  prediction?: Record<string, Record<string, number>>
) {
  const soilEval = prediction?.[condition.category]?.[condition.value];
  if (!soilEval) return false;
  return soilEval >= condition.minConfidence;
}

export function evaluateEdgeCondition(condition: EdgeConditionDTO, prediction?: number[][]) {
  if (!prediction || prediction.length === 0) return false;

  const count = prediction
    .map((row) => Math.max(...row))
    .filter((maxValue) => {
      switch (condition.operator) {
        case '<':
          return maxValue < condition.value;
        case '<=':
          return maxValue <= condition.value;
        case '>':
          return maxValue > condition.value;
        case '>=':
          return maxValue >= condition.value;
        default:
          return false;
      }
    }).length;

  if (condition.thresholdMode === 'min') return count >= condition.thresholdCount;
  return count <= condition.thresholdCount;
}
